// Shared by the profile's Compensation section, the revision comparison and the
// Finance verification queue, so all three group, label and total a breakdown the same way.

// Earnings of the Standard structure (CEA, HOSTEL, VEHICLE …), plus TA, MEDICAL, CAR, FOOD and
// CEA_CHA — display-only, so revisions saved by the original engine (before the Standard
// structure) still total and compare correctly. New revisions never produce those keys.
// amountOf() returns 0 for a key a snapshot doesn't carry, so listing extra keys is harmless.
export const EARN_KEYS = ["BASIC","HRA","LTA","TEL","CEA","HOSTEL","VEHICLE","TA","MEDICAL","CEA_CHA","CAR","FOOD","BONUS","OTHER_ALLOWANCES"];
export const DED_KEYS  = ["PF_EMPLOYEE","ESI_EMPLOYEE","MEDICAL_INSURANCE","INCOME_TAX","PROF_TAX"];
export const EMP_KEYS  = ["PF_EMPLOYER","ESI_EMPLOYER","GRATUITY"];

export const COMP_LABELS: Record<string, string> = {
  // Production structure (#1-19)
  BASIC:"Basic Salary",HRA:"HRA",LTA:"LTA",TEL:"Tel & Internet",
  CEA:"Children Education",HOSTEL:"Children Hostel",VEHICLE:"Vehicle Maintenance",
  OTHER_ALLOWANCES:"Other Allowances",BONUS:"Bonus",
  // Legacy structure (#20) — kept so older snapshots still read correctly
  TA:"Travel Allowance",MEDICAL:"Medical Allowance",CEA_CHA:"CEA & CHA",CAR:"Car Maintenance",FOOD:"Food",
  // Deductions + employer cost (shared)
  PF_EMPLOYEE:"PF (Employee)",ESI_EMPLOYEE:"ESI (Employee)",
  MEDICAL_INSURANCE:"Medical Insurance",INCOME_TAX:"Income Tax",PROF_TAX:"Prof. Tax",
  PF_EMPLOYER:"PF (Employer)",ESI_EMPLOYER:"ESI (Employer)",GRATUITY:"Gratuity",
};

export const fmtAmt = (n: number) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);
export const asNum = (v: number | string | null | undefined) =>
  v == null ? 0 : typeof v === "number" ? v : parseFloat(v);
export const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
export const humanize = (s: string) => s.replace(/_/g, " ");

export interface BreakdownAmount { componentKey: string; annualAmount: number | string; monthlyAmount: number | string }

export function amountOf(lines: BreakdownAmount[], key: string): number {
  return asNum(lines.find(l => l.componentKey === key)?.annualAmount);
}

/** Annual gross (all earnings) and net (gross less employee deductions). */
export function totalsOf(lines: BreakdownAmount[]) {
  const gross = EARN_KEYS.reduce((s, k) => s + amountOf(lines, k), 0);
  const deductions = DED_KEYS.reduce((s, k) => s + amountOf(lines, k), 0);
  return { gross, net: gross - deductions };
}

/**
 * The workbook's "Take Home before tax" (annual), matching the Excel sheet:
 * Gross − Gratuity − Total PF − Total ESI − Medical Insurance. Every employee uses the
 * Standard structure, so there is one formula.
 */
export function takeHomeBeforeTax(lines: BreakdownAmount[]): number {
  const g = (k: string) => amountOf(lines, k);
  const grossAll = EARN_KEYS.reduce((s, k) => s + amountOf(lines, k), 0); // includes bonus
  // Bonus is a yearly payout — never part of monthly in-hand — so it is excluded.
  const grossExclBonus = grossAll - g("BONUS");
  return grossExclBonus - g("GRATUITY") - g("PF_EMPLOYEE") - g("PF_EMPLOYER")
    - g("ESI_EMPLOYEE") - g("ESI_EMPLOYER") - g("MEDICAL_INSURANCE");
}

// ── Finance verification status ──────────────────────────────────────────────

export type CompStatus = "PENDING_VERIFICATION" | "REJECTED" | "APPROVED" | "WITHDRAWN";

export const COMP_STATUS_BADGE: Record<CompStatus, { label: string; tone: "warning" | "error" | "success" | "neutral" }> = {
  PENDING_VERIFICATION: { label: "Unconfirmed · with Finance", tone: "warning" },
  REJECTED:             { label: "Rejected by Finance",        tone: "error" },
  APPROVED:             { label: "Verified",                   tone: "success" },
  WITHDRAWN:            { label: "Withdrawn",                  tone: "neutral" },
};

export const isOpenStatus = (s: string) => s === "PENDING_VERIFICATION" || s === "REJECTED";

export const REVIEW_ACTION_LABELS: Record<string, string> = {
  SUBMITTED:   "Submitted to Finance",
  RESUBMITTED: "Edited and resubmitted",
  APPROVED:    "Approved by Finance",
  REJECTED:    "Rejected by Finance",
  WITHDRAWN:   "Withdrawn",
  UNLOCKED:    "Unlocked by HR",
  RECALCULATED: "Recalculated with new structure rules",
  SUPERSEDED:   "Replaced by newer structure rules",
};
